<script setup lang="ts">
import { ref } from 'vue';

import { useNuxtApp } from '#app';
import { computed, useProviderConfig } from '#imports';
import CollectionStats from '~/components/entities/collection/CollectionStats.vue';
import Marked from '~/components/shared/Markdown.vue';

const props = defineProps<{
    collectionInfo?: AppWTFCollection;
    isLoading?: boolean;
}>();

const filteredLinks = props.collectionInfo?.links
    ? props.collectionInfo?.links.filter(link => link.title !== 'opensea' && link.title !== 'instagram').map((link) => {
        if (link.title === 'twitter') {
            return {
                ...link,
                href: `https://twitter.com/${link.href}`
            };
        }
        return link;
    })
    : [];

const shortOwnerAddress = computed(() => {
    return props.collectionInfo?.owner.slice(
        0,
        8
    ) + (props.collectionInfo && props.collectionInfo?.owner.length > 8
        ? '...'
        : '');
});

const isExpanded = ref(false);
const descriptionRef = ref<HTMLElement | null>(null);

const { '$gsap': gsap } = useNuxtApp();

const {
    links,
    map: { chainMap }
} = useProviderConfig()(['links', 'map']);

const isVisibleReadMore = computed(() => {
    const limit = 18;
    const textWordCount = props.collectionInfo?.description.split(/\s+/).length || 0;
    return textWordCount > limit;
});

const toggleReadMore = () => {
    isExpanded.value = !isExpanded.value;
    const element = descriptionRef.value;

    if (element) {
        if (isExpanded.value) {
            gsap.to(
                element,
                {
                    height: element.scrollHeight + 'px',
                    duration: 0.5,
                    ease: 'power2.inOut',
                    onComplete: () => {
                        element.style.height = 'auto';
                    }
                }
            );

            /*
* gsap.to(iconElement, {
*     rotation: 180,
*     duration: 0.5,
*     ease: 'power2.inOut'
* });
*/
        }
        else {
            gsap.to(
                element,
                {
                    height: '84px',
                    duration: 0.5,
                    ease: 'power2.inOut'
                }
            );

            /*
* gsap.to(iconElement, {
*     rotation: 0,
*     duration: 0.5,
*     ease: 'power2.inOut'
* });
*/
        }
    }
};

</script>

<template>
  <div class="collection-title-grid">
    <div class="collection-img-container overflow-hidden">
      <SkeletonMain v-if="isLoading"
                    class="main-image"
      />
      <ImgMain v-else
               class="main-image"
               :src="props.collectionInfo?.imageUrl"
               :alt="props.collectionInfo?.name"
               quality="90"
               sizes="64px lg:120px xl:200px xxl:280px xxxl:280px"
               densities="x1 x2"
      />
    </div>

    <div class="collection-title-w">
      <SkeletonMain :height="64"
                    v-if="isLoading"
                    class="row width-full"
      />
      <div v-else
           class="row"
      >
        <h1 class="display-800-accent">
          {{ props.collectionInfo?.name }}
        </h1>

        <div class="display-flex align-items-center gap-16 flex-wrap">
          <NuxtLink :to="`/profile/${collectionInfo?.owner}`"
                    class="compact-600-default color-text-grey"
          >
            Created by <span class="span-color">{{ shortOwnerAddress }}</span>
          </NuxtLink>
          <div class="display-flex align-items-center gap-4 flex-wrap">
            <a v-for="link in filteredLinks"
               :key="link.title"
               :href="link.href"
               target="_blank"
               rel="noopener noreferrer"
               class="display-flex align-items-center justify-center title-link"
            >
              <IconMain :icon="link.title === 'project' ? 'external-window' : link.title"
                        :size="16"
              />
            </a>
            <a v-if="collectionInfo?.address"
               target="_blank"
               rel="noopener noreferrer"
               class="display-flex align-items-center justify-center title-link"
               :href="`${links.scanner[collectionInfo.network].address}${collectionInfo.address}`"
            >
              <IconMain :icon="links.scanner[collectionInfo.network].icon"
                        :size="16"
              />
              <span class="compact-500-accent ml-8"
                    style="
                            display: -webkit-box;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            word-break: break-all;
                            -webkit-line-clamp: 1;
                            -webkit-box-orient: vertical;
                          "
              >
                View on {{ chainMap[collectionInfo.network].name }}
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="collection-description">
      <SkeletonMain v-if="isLoading"
                    :height="126"
                    class="width-full"
      />
      <div v-else-if="collectionInfo?.description"
           class="display-flex flex-column gap-4"
      >
        <div ref="descriptionRef"
             style=" height: 84px;overflow: hidden;"
        >
          <Marked class="text-600-default color-text-grey"
                  :inline="false"
                  :source="collectionInfo.description"
                  tag="div"
                  :unsafe="true"
          />
        </div>

        <Button v-if="isVisibleReadMore"
                size="size.small"
                color="bg.text"
                class="collection-description__more"
        >
          <button type="button"
                  class="compact-400-accent text-color-primary display-flex"
                  @click="toggleReadMore"
          >
            <span>{{ isExpanded ? 'Read less' : 'Read more' }}</span>

            <IconMain v-if="isExpanded"
                      icon="chevron-down"
                      :size="20"
                      class="chevron-icon ml-4"
            />
            <IconMain v-else
                      icon="chevron-down"
                      :size="20"
                      class="ml-4"
            />
          </button>
        </Button>
      </div>

      <SkeletonMain v-if="isLoading"
                    :height="46"
                    class="collection-items"
      />
      <div v-else-if="collectionInfo"
           class="collection-items"
      >
        <CollectionStats :items="collectionInfo.totalSupply"
                         :owners="collectionInfo.stats.numOwners"
                         :floor="collectionInfo.floorPrice"
                         :volume="collectionInfo.stats.volumeCount.h24"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>

.no-wrap {
  text-wrap: nowrap;
}

.collection-title-grid {
  display: grid;
  grid-auto-columns: 1fr;
  grid-column-gap: 32px;
  grid-row-gap: 16px;
  grid-template:
        "Area"
        "." / 280px 1fr;

  @include desktop-1440 {
    grid-template-columns: 200px 1fr;
  }

  @include desktop-1200 {
    grid-template-columns: 160px 1fr;
  }

  @include tablet-768 {
    grid-template-columns: 64px 1fr;
  }
}

.collection-img-container {
  grid-area: span 2 / span 1 / span 2 / span 1;
  width: 280px;
  max-width: 280px;
  height: 280px;
  max-height: 280px;
  border-radius: 50%;

  @include desktop-1440 {
    max-width: 200px;
    max-height: 200px;
  }

  @include desktop-1200 {
    max-width: 160px;
    max-height: 160px;
  }

  @include tablet-768 {
    grid-area: span 1 / span 1 / span 1 / span 1;
    width: 64px;
    max-width: 64px;
    height: 64px;
    max-height: 64px;
  }
}

.collection-title-w {
  display: grid;
  grid-auto-columns: 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
  align-self: end;

  @include tablet-768 {
    display: flex;
  }
}

.row {
  display: flex;
  grid-column-gap: 8px;
  grid-row-gap: 8px;
  flex-flow: column;
}

.collection-description {
  display: grid;
  grid-auto-columns: 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 16px;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto;
  align-self: start;

  &__more {
    width: fit-content;
    margin-left: -12px;
  }

  @include desktop-1200 {
    grid-template-columns: 1fr;

    .collection-items {
      order: 2;
      place-self: start;
    }
  }

  @include tablet-768 {
    grid-area: span 1 / span 2 / span 1 / span 2;
  }

}

.collection-items {
  width: 100%;
  max-width: 400px;
  place-self: end end;
}

.main-image {
  width: 100%;
  border-radius: 100%;
}

.span-color {
  color: var(--content-brand-aviation);
}

.title-link {
  min-width: var(--control-300-size-single, 28px);
  padding: 5px;
  transition: opacity 0.3s ease-in-out;
  border-radius: var(--control-500-radius, 2px);
  background: var(--bg-secondary);

  &:hover {
    opacity: 0.4;
  }
}

.chevron-icon {
  rotate: 180deg;
}

//.text-text-size {
//  @include mobile-576 {
//    width: 340px;
//  }
//}

</style>
